import {HoistModel, managed, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {Distribution, DistributionActionEnum, Portfolio, Security} from '../../../data/DataTypes';
import {UserName} from '../../../../core/DataTypes';
import {getDefaultOptions} from '../../../../core/ServiceUtils';
import {FormModel} from '@xh/hoist/cmp/form';
import {numberIs, required} from '@xh/hoist/data';
import {LocalDate} from '@xh/hoist/utils/datetime';
import {GridModel} from '@xh/hoist/cmp/grid';
import {fmtPercent, fmtPrice} from '@xh/hoist/format';
import {orderBy} from 'lodash';

export class AddDistributionDialogModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen = false;
    @managed formModel = this.createFormModel();
    @managed gridModel = this.createGridModel();
    private resolver: (value: Distribution[]) => void;
    @observableRef accessor securities: Security[] = [];
    private users: Map<number, UserName> = new Map();
    private portfolios: Map<number, Portfolio> = new Map();

    @action openAsync(): Promise<Distribution[]> {
        this.formModel.init({});
        this.gridModel.clear();
        this.resolver = () => {};
        this.isOpen = true;
        this.loadDomainDataAsync().linkTo(this.apiTask);

        return new Promise(r => (this.resolver = r));
    }

    @action close() {
        this.isOpen = false;
        this.resolver(null);
    }

    async process() {
        const {securityId, amount, posted} = this.formModel.getData();

        try {
            const distributions = await this.processDistributionAsync({
                securityId,
                portfolioId: 0,
                ownerUserId: 0,
                id: 0,
                action: DistributionActionEnum.Posted,
                amount,
                posted
            }).linkTo(this.apiTask);

            this.gridModel.loadData(
                distributions.map(d => ({
                    ...d,
                    id: XH.genId(),
                    portfolioName: this.portfolios.get(d.portfolioId)?.portfolioName,
                    userName: this.users.get(this.portfolios.get(d.portfolioId).userId)?.username
                }))
            );
        } catch (e) {
            XH.handleException(e, {title: 'Unable to proccess distribution'});
        }
    }

    @action
    async save() {
        this.isOpen = false;
        const distributions = this.gridModel.store.allRecords.map(r => r.raw as Distribution);
        this.resolver(distributions);
    }

    get securitieOptions(): SelectOption[] {
        return orderBy(
            this.securities.map(s => ({value: s.id, label: s.name})),
            'label'
        );
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {name: 'securityId', rules: [required]},
                {name: 'amount', rules: [required, numberIs({min: 1})]},
                {name: 'posted', initialValue: LocalDate.today(), rules: [required]}
            ]
        });
    }

    private createGridModel(): GridModel {
        return new GridModel({
            columns: [
                {field: 'id', hidden: true},
                {field: 'userName', headerName: 'Entity', flex: 1},
                {field: 'portfolioId', hidden: true},
                {field: 'portfolioName', headerName: 'Portfolio', flex: 1},
                {field: 'securityId', hidden: true},
                {
                    field: 'allicationPct',
                    headerName: 'Allocation %',
                    align: 'right',
                    width: 100,
                    renderer: v => fmtPercent(v, {precision: 2})
                },
                {
                    field: 'amount',
                    align: 'right',
                    width: 120,
                    renderer: v => fmtPrice(v, {precision: 2, prefix: '$'})
                },
                {field: 'action', width: 120}
            ]
        });
    }

    private async loadDomainDataAsync() {
        const securities = await XH.secMasterService.searchSecuritiesAsync('%');
        const portfolios = await XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/all`,
            ...getDefaultOptions()
        });
        const users = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/users/names`,
            ...getDefaultOptions()
        });

        runInAction(() => {
            this.securities = securities;
            this.portfolios = new Map(portfolios.map(p => [p.id, p]));
            this.users = new Map(users.map(u => [u.id, u]));
        });
    }

    private processDistributionAsync(distribution: Distribution): Promise<Distribution[]> {
        return XH.postJson({
            url: `${XH.getConf('svcApiUrl')}/realestate/distributions/process`,
            body: distribution,
            ...getDefaultOptions()
        });
    }
}
